import { useMemo, useState } from 'react'
import HomeRoundedIcon from '@mui/icons-material/HomeRounded'
import MenuBookRoundedIcon from '@mui/icons-material/MenuBookRounded'
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded'
import ChecklistRoundedIcon from '@mui/icons-material/ChecklistRounded'
import MoreHorizRoundedIcon from '@mui/icons-material/MoreHorizRounded'
import { MobileTabBar, MobileTabRow, type MobileTabItem } from './Common/MobileTabs'
import { MobileFullScreenPanel } from './Common/MobileFullScreenPanel'
import { Pressable } from './Common/Pressable'
import { OverflowTip } from './Common/Hover/OverflowTip'
import { CountBadge } from './Common/Chip'
import { NotificationHub } from './Notifications/NotificationHub'
import { useWorkspace } from '../context/useWorkspace'
import { useNotifications } from '../context/useNotifications'
import { useNls } from '../nls/nlsProvider'
import {
  buildRightRailNavItems,
  type RightSideNavProps,
  type RailTileState,
} from './RightSideNav'
import {
  DOCK_VIEW_LABELS,
  type DashboardView,
} from './Common/mobileDashboardDockConstants'

export type RightSideNavMobileProps = {
  railProps: RightSideNavProps
  activeLayoutView: string
  activeDashboardView: DashboardView | null
  dockEnabledViews: DashboardView[]
  onSelectDashboardView: (view: DashboardView) => void
  onHeightChange?: (height: number) => void
}

const PRIMARY_KEYS = new Set(['OVERVIEW', 'DASHBOARD', 'CHAT', 'CHECKLISTS'])

export const RightSideNavMobile = ({
  railProps,
  activeLayoutView,
  activeDashboardView,
  dockEnabledViews,
  onSelectDashboardView,
  onHeightChange,
}: RightSideNavMobileProps) => {
  const { t } = useNls()
  const { activeWorkspaceId, workspaces } = useWorkspace()
  const activeWorkspace = workspaces.find((w) => w.id === activeWorkspaceId)
  const { notifications } = useNotifications()
  const notificationsBadgeCount = notifications.filter((n) => !n.readAt).length

  const [isMoreOpen, setIsMoreOpen] = useState(false)
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false)

  const openNotificationsView = () => {
    setIsMoreOpen(false)
    setIsNotificationsOpen(true)
  }

  const { boardTiles, controlsTiles } = useMemo(
    () =>
      buildRightRailNavItems(railProps, {
        activeWorkspace,
        t,
        notificationsBadgeCount,
        openNotificationsView,
        isNotificationsOpen,
      }),
    [railProps, activeWorkspace, t, notificationsBadgeCount, isNotificationsOpen],
  )

  // Subtab items for Dashboard tab (B3)
  const dashboardSubtabs = useMemo<MobileTabItem<DashboardView>[]>(() => {
    const tabs: MobileTabItem<DashboardView>[] = []
    for (const view of dockEnabledViews) {
      const item = DOCK_VIEW_LABELS[view]
      if (item) {
        tabs.push({
          key: view,
          label: item.label,
          icon: <span aria-hidden="true">{item.icon}</span>,
        })
      }
    }
    return tabs
  }, [dockEnabledViews])

  // Primary tabs (B2): Overview (only when available on rail), Dashboard, Chat (only when available on rail), Lists
  const overviewTile = boardTiles.find((tile) => tile.key === 'OVERVIEW')
  const dashboardTile = boardTiles.find((tile) => tile.key === 'DASHBOARD')
  const chatTile = boardTiles.find((tile) => tile.key === 'CHAT')
  const checklistsTile = boardTiles.find((tile) => tile.key === 'CHECKLISTS')

  const primaryItems: MobileTabItem[] = useMemo(() => {
    const items: MobileTabItem[] = []

    if (overviewTile && overviewTile.onClick) {
      items.push({
        key: 'OVERVIEW',
        label: overviewTile.shortLabel ?? overviewTile.label,
        icon: <MenuBookRoundedIcon fontSize="small" />,
      })
    }

    items.push({
      key: 'DASHBOARD',
      label: dashboardTile?.shortLabel ?? t('NLSShellRailShortDashboard'),
      icon: <HomeRoundedIcon fontSize="small" />,
    })

    if (chatTile && chatTile.onClick) {
      items.push({
        key: 'CHAT',
        label: chatTile.shortLabel ?? chatTile.label,
        icon: <ChatBubbleOutlineRoundedIcon fontSize="small" />,
      })
    }

    items.push({
      key: 'CHECKLISTS',
      label: checklistsTile?.shortLabel ?? t('NLSShellRailShortChecklists'),
      icon: <ChecklistRoundedIcon fontSize="small" />,
    })

    return items
  }, [overviewTile, dashboardTile, chatTile, checklistsTile, t])

  const isDashboardTab = activeLayoutView === 'DASHBOARD'
  const isPrimaryActive = PRIMARY_KEYS.has(activeLayoutView)
  const isMoreActive = isMoreOpen || !isPrimaryActive

  const handlePrimaryTabChange = (key: string) => {
    if (key === 'OVERVIEW') overviewTile?.onClick?.()
    else if (key === 'DASHBOARD') dashboardTile?.onClick?.()
    else if (key === 'CHAT') chatTile?.onClick?.()
    else if (key === 'CHECKLISTS') checklistsTile?.onClick?.()
  }

  // Remaining destinations for More panel
  const remainingTiles = useMemo(
    () => [...boardTiles, ...controlsTiles].filter((tile) => !PRIMARY_KEYS.has(tile.key)),
    [boardTiles, controlsTiles],
  )

  const handleTileClick = (tile: RailTileState) => {
    if (tile.disabled || !tile.onClick) return
    setIsMoreOpen(false)
    tile.onClick()
  }

  return (
    <>
      <MobileTabBar
        ariaLabel="Console Navigation"
        hasSecondaryRow={isDashboardTab}
        onHeightChange={onHeightChange}
        surface="console-phone"
        layer="shell"
      >
        {isDashboardTab && (
          <MobileTabRow
            placement="bottom"
            variant="pills"
            overflowStrategy="scroll"
            ariaLabel="Dashboard views"
            items={dashboardSubtabs}
            activeKey={activeDashboardView ?? 'Timeline'}
            onChange={(key) => onSelectDashboardView(key as DashboardView)}
          />
        )}
        <MobileTabRow
          ariaLabel="Main navigation"
          items={primaryItems}
          activeKey={isPrimaryActive ? activeLayoutView : undefined}
          onChange={handlePrimaryTabChange}
          more={{
            label: 'More',
            ariaLabel: 'More destinations',
            icon: <MoreHorizRoundedIcon fontSize="small" />,
            isActive: isMoreActive,
            onClick: () => setIsMoreOpen(true),
            unread: notificationsBadgeCount > 0,
          }}
        />
      </MobileTabBar>

      {isMoreOpen && (
        <MobileFullScreenPanel
          title="More"
          uiName="UI-CONSOLE-PHONE-MORE"
          onClose={() => setIsMoreOpen(false)}
          closeLabel="Close"
          layer="dialog"
        >
          <div
            className="consolePhoneMoreGrid"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))',
              gap: '10px',
              padding: '6px 0',
            }}
          >
            {remainingTiles.map((tile) => {
              const isActive = tile.active
              return (
                <Pressable
                  key={tile.key}
                  onClick={() => handleTileClick(tile)}
                  disabled={tile.disabled || !tile.onClick}
                  aria-label={tile.label}
                  aria-current={isActive ? 'page' : undefined}
                  data-tour-id={tile.tourId}
                  className={`consolePhoneMoreTile ${isActive ? 'isActive' : ''}`}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    padding: '14px 8px',
                    borderRadius: 'var(--radius-card)',
                    border: '1px solid var(--border)',
                    backgroundColor: isActive
                      ? 'color-mix(in srgb, var(--accent) 14%, var(--surface))'
                      : 'var(--surface)',
                    color: isActive ? 'var(--accent-strong)' : 'var(--text-strong)',
                    opacity: tile.disabled ? 0.45 : 1,
                    textAlign: 'center',
                    minHeight: '84px',
                    position: 'relative',
                  }}
                >
                  <span
                    className="consolePhoneMoreTile__icon"
                    aria-hidden="true"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      position: 'relative',
                    }}
                  >
                    {tile.icon}
                    {tile.indicator ? (
                      <span
                        style={{
                          position: 'absolute',
                          top: -2,
                          right: -2,
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          backgroundColor: 'var(--danger)',
                        }}
                        aria-hidden="true"
                      />
                    ) : null}
                  </span>
                  <OverflowTip label={tile.label}>
                    <span
                      style={{
                        fontSize: '12px',
                        fontWeight: isActive ? 600 : 500,
                        lineHeight: 1.2,
                        maxWidth: '100%',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {tile.shortLabel ?? tile.label}
                    </span>
                  </OverflowTip>
                  {tile.badge && tile.badge > 0 ? (
                    <span style={{ position: 'absolute', top: 6, right: 6 }}>
                      <CountBadge
                        value={tile.badge}
                        max={99}
                      />
                    </span>
                  ) : null}
                </Pressable>
              )
            })}
          </div>
        </MobileFullScreenPanel>
      )}

      {isNotificationsOpen && (
        <MobileFullScreenPanel
          title={t('NLSShellRailTileNotifications')}
          uiName="UI-CONSOLE-PHONE-NOTIFICATIONS"
          onClose={() => setIsNotificationsOpen(false)}
          layer="dialog"
          className="consolePhoneNotifsPanel"
        >
          <div className="consolePhoneNotifs">
            <NotificationHub
              onClose={() => setIsNotificationsOpen(false)}
              notificationRouter={railProps.notificationRouter}
            />
          </div>
        </MobileFullScreenPanel>
      )}
    </>
  )
}
